import { Button } from "reactstrap";
import Sidenav from "../../../navbar/Sidenav";
import { ArrowRightIcon, BackIcon, categoryIconMap, ChipsetIcon, ChipsetIcon2, CreditCardIcon, CustomisePlanIcon, DatasetIcon, EyeIcon, HistoryIcon, InfoIcon, LabelsIcon, MenuIcon, ModelEvaluationIcon, ModelTrainingIcon, PlusIcon, TableIcon, VersionsIcon2 } from "../../../Common/SvgIcons/WorkflowIcons";
import "./PlansBilling.scss";
import useFetch from "../../../../components/useFetch";
import { useLocation, useNavigate } from "react-router-dom";
import { width } from "@mui/system";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import { getCokkie } from "../../../login/Login";
import VersionDetail2 from "../../../project/data/version_detail/VersionDetail2";
import { Skeleton } from "@mui/material";
import TitleHeader from "../../../project/header/TitleHeader";
import getDataFromApi from "../../../../components/getDataFromApi";
import PathBreadcrumb from "../../../../components/PathBreadcrumb/PathBreadcrumb";

export default function PlansBilling(props) {
  const [search, setSearch] = useState("");
  const [upgradingPlanId, setUpgradingPlanId] = useState(null);
  const [confirmPlan, setConfirmPlan] = useState(null);
  const [billingMissingModal, setBillingMissingModal] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(6);

  const navigate = useNavigate();
  const role = localStorage.getItem("role");
  const location = useLocation();

  const tenantId = location.state?.tenantId;
  const currentPlanId = location.state?.planId;
  const currentPlanName = location.state?.planName;
  const SubStaus = location.state?.status;

  const url = props?.viewFromDetails ? `admin/plan?page=1&per_page=3` : currentPlanId && tenantId ? `admin/plan?${new URLSearchParams({ filter: "is_active=true", }).toString()}` : `admin/plan`;

  const { data, loading, error, setData } = useFetch(url, "GET");

  const plans = data?.results || [];

  useEffect(() => {
    if (!props?.viewFromDetails || !props?.silentRefreshKey) return;

    getDataFromApi(`${window.ENV.API_URL}/${url}`, "get")
      .then((freshData) => {
        setData(freshData);
      })
      .catch((err) => {
        console.error("Failed to silently refresh plans", err);
      });
  }, [props?.viewFromDetails, props?.silentRefreshKey, setData, url]);

  // Get all unique categories across all plans
  const allCategories = [
    ...new Set(
      plans.flatMap((plan) => [
        ...Object.keys(plan.included_features || {}),
        ...Object.keys(plan.tenant_resources || {}),
        ...Object.keys(plan.user_resources || {}),
      ])
    ),
  ];




  const getCategoryIcon = (category) => {
    const Icon = categoryIconMap[category?.toLowerCase()] || MenuIcon;

    return <Icon className="wf-icon p-1 border-0" style={{ width: 25, height: 25 }} />;
  };


  useEffect(() => {
    if (!billingMissingModal) return;

    setRedirectCountdown(6);

    const interval = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate(-1); // change this if your dashboard route is different
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [billingMissingModal, navigate]);


  const isBillingDetailsMissingError = (result) => {
    if (!result) return false;

    // direct match
    if (
      typeof result?.detail === "string" &&
      result.detail.toLowerCase().includes("billing details not found")
    ) {
      return true;
    }

    // sometimes backend wraps JSON string inside detail
    try {
      if (typeof result?.detail === "string") {
        const jsonPart = result.detail.replace(/^HTTP \d+:\s*/, "");
        const parsed = JSON.parse(jsonPart);

        if (
          typeof parsed?.detail === "string" &&
          parsed.detail.toLowerCase().includes("billing details not found")
        ) {
          return true;
        }
      }
    } catch (e) {
      // ignore parse failures
    }

    return false;
  };



  const handleUpgradePlan = async (newPlanId) => {
    try {
      setUpgradingPlanId(newPlanId);

      const payload = {
        tenant_id: tenantId,
        plan_id: newPlanId,
      };

      const token = getCokkie("jwtToken");

      const response = await fetch(
        `${window.ENV.API_URL}/admin/subscription/upgrade`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        if (isBillingDetailsMissingError(result)) {
          setBillingMissingModal(true);
          return;
        }

        throw new Error(result?.message || result?.detail || "Upgrade failed");
      }

      if (result?.type === "checkout") {
        navigate(result?.url);
      } else {
        navigate(`/admin/success?tenant_id=${tenantId}&plan_name=${currentPlanName}`, {
          replace: true,
          state: {
            title:
              SubStaus === "Subscribe Again"
                ? "Subscription Activated"
                : "Plan Upgraded",
            message:
              SubStaus === "Subscribe Again"
                ? "Your subscription has been activated successfully."
                : "Your plan has been upgraded successfully.",
            redirect: {
              path: "/admin/tenant/details",
              state: { id: tenantId },
            },
            seconds: 8,
          },
        });
      }
    } catch (error) {
      console.error("Upgrade error:", error);
      alert(error.message);
    } finally {
      setUpgradingPlanId(null);
    }
  };

  const getFeatureCountByCategory = (plan, category) => {
    return plan.included_features?.[category] || 0;
  };


  const filteredPlans = plans.filter((plan) => {
    if (!search.trim()) return true;

    const searchTerm = search.toLowerCase();

    // Convert plan to searchable string
    const searchableContent = [
      plan.name,
      plan.plan_type,
      plan.billing_price?.toString(),
      plan.billing_cycle,
      "active",      // since you hardcoded Active
      "public",      // since you hardcoded Public
      ...Object.entries(plan.included_features || {}).map(
        ([category, count]) => `${category} ${count}`
      ),
    ]
      .join(" ")
      .toLowerCase();

    return searchableContent.includes(searchTerm);
  });




  const categoryOrder = [
    "core",
    "data",
    "label",
    "labeling",
    "training",
    "deployment",
    "evaluation",
    "solution",
    "solutions",
    "monitoring",
  ];

  const sortCategories = (categories = []) => {
    return [...categories].sort((a, b) => {
      const indexA = categoryOrder.indexOf(a);
      const indexB = categoryOrder.indexOf(b);

      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  };
  // Sort allCategories once
  const orderedAllCategories = sortCategories(
    allCategories.map(cat => cat?.toLowerCase())
  );


  const getResourceCountByCategory = (plan, category) => {
    const tenantCount = Number(plan.tenant_resources?.[category] || 0);
    const userCount = Number(plan.user_resources?.[category] || 0);
    return tenantCount + userCount;
  };


  if (loading) {
    if (props?.viewFromDetails) {
      return (
        <div className="d-a flex-column gap-3 mt-4">
          {[...Array(3)].map((_, idx) => (
            <div
              key={idx}
              className="plan-card-new container-card h-100 d-flex flex-column justify-content-between w-100"
            >
              <div>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <Skeleton variant="text" width={140} height={30} />
                    <Skeleton variant="text" width={90} height={20} />
                  </div>

                  <div className="text-end">
                    <Skeleton variant="text" width={70} height={34} />
                    <Skeleton variant="text" width={60} height={20} />
                  </div>
                </div>

                <div className="mt-4">
                  <div className="d-flex align-items-center gap-2">
                    <Skeleton variant="circular" width={35} height={35} />
                    <Skeleton variant="text" width={120} height={24} />
                  </div>

                  <div className="d-flex align-items-center gap-2 mt-2">
                    <Skeleton variant="circular" width={35} height={35} />
                    <div>
                      <Skeleton variant="text" width={130} height={24} />
                      <Skeleton variant="text" width={170} height={18} />
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2 mt-3">
                  {[...Array(4)].map((_, pillIdx) => (
                    <Skeleton
                      key={pillIdx}
                      variant="rounded"
                      width={70}
                      height={28}
                    />
                  ))}
                </div>
              </div>

              <Skeleton
                variant="rounded"
                width="100%"
                height={40}
                className="mt-4"
              />
            </div>
          ))}
        </div>
      );
    }

    return (
      <>

        <div className="mainmenu projects-content de-width">
          <PathBreadcrumb
            onBack={() => { tenantId ? navigate(-1) : navigate('/admin') }}
            items={
              tenantId
                ? [
                  { label: 'Admin', onClick: () => navigate('/admin') },
                  { label: 'Tenant', onClick: () => navigate(-1) },
                  { label: 'Plans' },
                ]
                : [
                  { label: 'Admin', onClick: () => navigate('/admin') },
                  { label: 'Plans' },
                ]
            }
          />

          <div className="d-a justify-content-between de-header-tag gap-3">
            <div className="d-a gap-3 flex-fill">
              <Skeleton
                variant="rounded"
                width={40}
                height={40}
                className="landing-page-main-icon flex-shrink-0"
              />
              <div className="title-header__copy">
                <Skeleton variant="text" width={220} height={36} />
                <Skeleton variant="text" width={280} height={22} className="mt-1" />
              </div>
            </div>
          </div>

          <div className="d-a gap-3 flex-column flex-sm-row justify-content-between my-4 w-100">
            <div className="d-a gap-2 p-2 badge-border px-3 flex-fill sub-nav-mb-button">
              <Skeleton variant="circular" width={18} height={18} />
              <Skeleton variant="text" width="100%" height={24} />
            </div>

            {!tenantId && (
              <Skeleton
                variant="rounded"
                width={170}
                height={42}
                style={{ borderRadius: "8px" }}
              />
            )}
          </div>

          <div className="row g-3 pe-0">
            {[...Array(8)].map((_, idx) => (
              <div key={idx} className="col-12 col-sm-6 col-lg-4 col-xl-3 pe-0">
                <div className="container-card h-100 plans-card-container">
                  <div className="d-a gap-2 justify-content-between">
                    <div className="d-flex gap-2">
                      <Skeleton variant="rounded" width={58} height={24} />
                      <Skeleton variant="rounded" width={70} height={24} />
                    </div>

                    <Skeleton variant="text" width={70} height={22} />
                  </div>

                  <Skeleton variant="text" width={120} height={28} className="mt-5" />

                  <div className="d-flex align-items-end mb-5">
                    <Skeleton variant="text" width={90} height={42} />
                    <Skeleton variant="text" width={50} height={22} />
                  </div>

                  <Skeleton variant="text" width={110} height={18} className="mb-2" />

                  {[...Array(5)].map((_, catIdx) => (
                    <div
                      key={catIdx}
                      className="d-a gap-2 container-card p-3 mt-2"
                      style={{
                        background: "var(--card-color)",
                        fontSize: "11.5px",
                      }}
                    >
                      <Skeleton variant="circular" width={25} height={25} />
                      <Skeleton variant="text" width="40%" height={22} />
                      <div className="ms-auto">
                        <Skeleton variant="text" width={80} height={22} />
                      </div>
                    </div>
                  ))}

                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={42}
                    className="mt-4"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mobile-view-bottom"></div>
      </>
    );
  }


  const hasError = !!error;
  const hasPlans = plans.length > 0;

  const noSearchResults =
    !loading &&
    hasPlans &&
    search.trim() &&
    filteredPlans.length === 0;


  if (!loading && hasError) {
    const status =
      error?.status ||
      error?.response?.status ||
      error?.response?.data?.status ||
      500;

    return (
      <>

        <div className="d-flex flex-column align-items-center justify-content-center h-100">
          <div
            style={{
              backgroundColor: "#fff8d5",
              border: "1px solid #ffe7a3",
              borderRadius: "4px",
              padding: "14px 24px",
              maxWidth: "600px",
              textAlign: "center",
              fontSize: "0.95rem",
              color: "black",
            }}
          >
            {status === 404
              ? "Plans not found (404)."
              : status === 403
                ? "You don’t have permission to view plans."
                : status === 500
                  ? "Server error (500). Please try again."
                  : "Unable to load plans right now."}
          </div>
        </div>
      </>
    );
  }



  if (!loading && !hasPlans) {
    return (
      <>

        <div className="d-flex flex-column align-items-center justify-content-center h-100">
          <div style={{ fontSize: "3rem" }}>💳</div>
          <h5 className="fw-semibold mt-3">No plans created yet</h5>
          <p className="text-secondary text-center" style={{ maxWidth: "400px" }}>
            You haven’t created any plans. Start by creating your first plan to configure your SaaS offerings.
          </p>
          <Button
            color="primary px-3 py-2 btn-click bg-main border-0"
            onClick={() => navigate("/admin/create-plan")}
          >
            Create New Plan
          </Button>
        </div>
      </>
    );
  }


  return (
    <>


      {props?.viewFromDetails ?

        <div className="d-a flex-column gap-3 mt-4">
          {filteredPlans.map((plan) => {
            const featureCount = Object.values(plan.included_features || {})
              .reduce((sum, count) => sum + count, 0);

            const tenantResourceCount = Object.keys(plan.tenant_resources || {}).length;
            const userResourceCount = Object.keys(plan.user_resources || {}).length;

            const totalResourceCount = tenantResourceCount + userResourceCount;


            const featureCategories = sortCategories(
              Object.keys(plan.included_features || {}).map(cat =>
                cat?.toLowerCase()
              )
            );


            return (
              <div className={`plan-card-new container-card h-100 d-flex flex-column justify-content-between w-100 ${props?.DetailedPlanId === plan?.id && 'd-none'}`}>

                {/* Recommended Badge (Optional) */}
                {plan.is_recommended && (
                  <div className="recommended-badge">Recommended</div>
                )}

                {/* Top Section */}
                <div>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h5 className="fw-bold text-capitalize m-0 text-primary">{plan.name}</h5>
                      <small className="text-secondary text-uppercase">
                        {plan.plan_type}
                      </small>
                    </div>

                    <div className="text-end">
                      <h3 className="fw-bold m-0">
                        ${plan.billing_price}
                      </h3>
                      <small className="text-secondary">
                        /{plan.billing_cycle?.toUpperCase()}
                      </small>
                    </div>
                  </div>


                  <div className="mt-4">

                    {/* Features */}
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      <VersionsIcon2 className='border-0 wf-icon-blue' style={{ padding: '7px', width: '35px', height: '35px' }} />
                      {featureCount} Feature{featureCount !== 1 && "s"}
                    </div>

                    {/* Resources */}
                    <div className="fw-semibold d-flex align-items-center gap-2 mt-2">
                      <ChipsetIcon className='border-0 wf-icon-green' style={{ padding: '10px', width: '35px', height: '35px' }} />
                      <div className="d-flex flex-column fw-semibold">
                        {totalResourceCount} Resource{totalResourceCount !== 1 && "s"}
                        {/* Resource Breakdown Description */}
                        {totalResourceCount > 0 && (
                          <div className="text-secondary small">
                            {tenantResourceCount > 0 && (
                              <>
                                {tenantResourceCount} Tenant Resource
                                {tenantResourceCount !== 1 && "s"}
                              </>
                            )}

                            {tenantResourceCount > 0 && userResourceCount > 0 && " • "}

                            {userResourceCount > 0 && (
                              <>
                                {userResourceCount} User Resource
                                {userResourceCount !== 1 && "s"}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                  </div>

                  {/* Category Pills */}
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    {featureCategories.map((category) => (
                      <span
                        key={category}
                        className="plan-pill text-capitalize rounded"
                      >
                        {category === "training"
                          ? "Train"
                          : category === "solutions"
                            ? "Solution"
                            : category === "labeling"
                              ? "Label"
                              : category}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Button */}
                <Button
                  color="primary"
                  className="w-100 mt-4 fw-semibold py-2"
                  onClick={() => setConfirmPlan(plan)}
                >
                  Upgrade to this plan
                </Button>
              </div>
            )
          })}
        </div>

        :



        <>

          <div className="mainmenu projects-content de-width">


            <PathBreadcrumb
              onBack={() => { tenantId ? navigate(-1) : navigate('/admin') }}
              items={
                tenantId
                  ? [
                    { label: 'Admin', onClick: () => navigate('/admin') },
                    { label: 'Tenant', onClick: () => navigate(-1) },
                    { label: 'Plans' },
                  ]
                  : [
                    { label: 'Admin', onClick: () => navigate('/admin') },
                    { label: 'Plans' },
                  ]
              }
            />


            <div className={`d-a justify-content-between pt-1 gap-3`}>
              <TitleHeader Icon={CreditCardIcon} title1={'Global'} title2={' Plan Catalogue'} subtext={!tenantId ? 'Manage your product tiers, pricing structures, and feature entitlements.' : `Select a plan that fits your growing organization's needs and feature requirements.`} />
              {role === 'system_admin' && !tenantId &&
                <Button
                  color="primary px-3 py-2 btn-click bg-main border-0  btn-full-width"
                  style={{ whiteSpace: "nowrap" }}
                  onClick={() => navigate('/admin/create-plan')}
                >
                  {/* <PlusIcon className="wf-icon text-white" /> */}
                  Create New Plan
                </Button>}
            </div>


            {!tenantId &&
              <div className="d-a gap-3 flex-column flex-sm-row justify-content-between mt-4 w-100">
                <div className="d-a gap-2 p-2 badge-border px-3 flex-fill sub-nav-mb-button">
                  <FontAwesomeIcon className="plain-button2 text-secondary" icon={faSearch} />
                  <input
                    className="border-0 m-0 bg-transparent text w-100"
                    style={{ outline: 'none' }}
                    placeholder="Search unit name, type, status..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>}



            <div className="row g-3 pe-0 mt-1">
              <div className="row g-3 pe-0">
                {noSearchResults ? (
                  <div className="d-flex flex-column align-items-center justify-content-center w-100 py-5">
                    <div style={{ fontSize: "3rem" }}>🔍</div>
                    <h5 className="fw-semibold mb-2">No matching plans found</h5>
                    <p className="text-secondary mb-3 text-center" style={{ maxWidth: "450px" }}>
                      Try adjusting your search.
                    </p>
                    <Button color="primary" onClick={() => setSearch("")}>
                      Clear Search
                    </Button>
                  </div>
                ) : (
                  <>
                    {tenantId &&
                      role === "tenant_admin" &&
                      !(filteredPlans.length === 1 && !filteredPlans[0]?.is_public) &&
                      <div className="col-12 col-sm-6 col-lg-4 col-xl-3 pe-0">
                        <div className="container-card shadow h-100 plans-card-container custom-plan-card d-flex flex-column justify-content-between text-center">
                          <div>
                            <div className="custom-plan-icon-wrap mx-auto">
                              <CustomisePlanIcon className="wf-icon text-white" />
                            </div>

                            <h4 className="fw-bold mt-4 mb-2">Build Your Own</h4>

                            <p className="text-secondary mb-4 px-2">
                              Need something specific? Create a custom plan tailored to your unique requirements.
                            </p>

                            <div className="d-flex flex-column align-items-start gap-2 custom-plan-features">
                              <div className="d-flex align-items-center gap-2">
                                <span className="custom-check">✓</span>
                                <span className="text-secondary small-font">Custom Feature Set</span>
                              </div>

                              <div className="d-flex align-items-center gap-2">
                                <span className="custom-check">✓</span>
                                <span className="text-secondary small-font">Scalable Resources</span>
                              </div>

                              <div className="d-flex align-items-center gap-2">
                                <span className="custom-check">✓</span>
                                <span className="text-secondary small-font">Priority Support</span>
                              </div>
                            </div>
                          </div>

                          <Button
                            color="primary"
                            className="w-100 mt-4 fw-semibold py-2"
                            onClick={() => navigate("/admin/create-plan", { state: { viewFrom: "customplan" } })}
                          >
                            Create Custom Plan
                          </Button>
                        </div>
                      </div>}

                    {/* Existing Plan Cards */}
                    {filteredPlans.map((plan) => {
                      const isCurrentPlan = String(plan.id) === String(currentPlanId);

                      return (
                        <div key={plan.id} className="col-12 col-sm-6 col-lg-4 col-xl-3 pe-0">
                          <div
                            className={
                              isCurrentPlan
                                ? "container-card position-relative"
                                : "container-card h-100 plans-card-container"
                            }
                            style={{
                              border: isCurrentPlan ? "3px solid var(--leading-color)" : "",
                            }}
                          >
                            {isCurrentPlan && (
                              <Button
                                className="wf-icon-blue px-2 py-0 mt-2 d-inline-block plain-wh text-center fw-bold bg-blue-icon"
                                style={{ position: "absolute", top: "-17px", right: 5 }}
                              >
                                Active plan
                              </Button>
                            )}

                            {/* Header */}
                            <div className="d-a gap-2 justify-content-between">
                              <div>
                                <span className={`border-0 ${plan?.is_active ? "wf-icon-green" : "wf-icon-red"} px-2 py-1 me-3`}>
                                  {plan?.is_active ? "Active" : "Inactive"}
                                </span>
                                <span className={`border-0 ${plan?.is_public ? "wf-icon-blue" : "wf-icon"} px-2 py-1`}>
                                  <EyeIcon className="wf-icon-small me-1" /> {plan?.is_public ? "Public" : "Private"}
                                </span>
                              </div>

                              <span className="text-secondary text-capitalize">{plan.plan_type}</span>
                            </div>

                            <div className="d-a gap-2 mt-5" onClick={() =>
                              navigate("/admin/plan-details", {
                                state: { planId: plan.id, tenantId: tenantId, viewFrom: !tenantId ? '' : "PlansUpgrade" },
                              })
                            }>
                              <h6 className="mb-0 text-capitalize">{plan.name}</h6>
                              {!isCurrentPlan && <BackIcon
                                className='wf-icon text-primary cursor-pointer'
                                style={{
                                  transform: 'rotate(140deg)',
                                  marginTop: '-5px'
                                }}
                              />}
                            </div>

                            <div className="d-flex align-items-end mb-5">
                              <h2 className="fw-bold m-0">${plan.billing_price}</h2>
                              <span className="text-secondary pb-1">/{plan.billing_cycle?.toUpperCase()}</span>
                            </div>

                            <span
                              className="text-secondary w-100 d-flex ps-1 pb-2"
                              style={{ fontSize: "11px" }}
                            >
                              MODULES INCLUDED
                            </span>


                            {orderedAllCategories.map((category) => {
                              const featureCount = Number(plan.included_features?.[category] || 0);
                              const tenantResourceCount = Number(plan.tenant_resources?.[category] || 0);
                              const userResourceCount = Number(plan.user_resources?.[category] || 0);
                              const resourceCount = tenantResourceCount + userResourceCount;

                              const hasAccess = featureCount > 0 || resourceCount > 0;

                              return (
                                <div
                                  key={category}
                                  className={`d-a gap-2 container-card p-3 mt-2 ${!hasAccess ? "opacity-50" : ""}`}
                                  style={{
                                    background: "var(--card-color)",
                                    fontSize: "11.5px",
                                  }}
                                >
                                  {getCategoryIcon(category)}

                                  <span className="fw-bold flex-fill text-capitalize">{category}</span>

                                  <span className={`fw-bold ${hasAccess ? "text-primary" : "text-secondary"}`}>
                                    {!hasAccess
                                      ? "No Access"
                                      : [
                                        featureCount > 0
                                          ? `${featureCount} Feature${featureCount !== 1 ? "s" : ""}`
                                          : null,
                                        resourceCount > 0
                                          ? `${resourceCount} Resource${resourceCount !== 1 ? "s" : ""}`
                                          : null,
                                      ]
                                        .filter(Boolean)
                                        .join(" • ")}
                                  </span>
                                </div>
                              );
                            })}

                            {tenantId ? (
                              isCurrentPlan ? (
                                <Button
                                  className="bg-transparent badge-border d-j-a w-100 gap-3 mt-4 fw-semibold py-2 container-card text-secondary"
                                  onClick={() =>
                                    navigate("/admin/plan-details", {
                                      state: { planId: plan.id, tenantId: tenantId, viewFrom: "PlansCurrentdetail" },
                                    })
                                  }
                                >
                                  View Plan Details
                                  <ArrowRightIcon className="wf-icon text-secondary" />
                                </Button>
                              ) : (
                                <Button
                                  color="primary"
                                  className="w-100 mt-4 fw-semibold py-2"
                                  onClick={() => setConfirmPlan(plan)}
                                  disabled={upgradingPlanId === plan.id}
                                >
                                  {upgradingPlanId === plan.id
                                    ? SubStaus === "Subscribe Again"
                                      ? "Subscribing..."
                                      : "Upgrading..."
                                    : SubStaus === "Subscribe Again"
                                      ? "Subscribe"
                                      : "Upgrade"}
                                </Button>
                              )
                            ) : (
                              <Button
                                className="bg-transparent badge-border d-j-a w-100 gap-3 mt-4 fw-semibold py-2 container-card text-secondary"
                                onClick={() =>
                                  navigate("/admin/plan-details", {
                                    state: { planId: plan.id, tenantId: tenantId, viewFrom: '' },
                                  })
                                }
                              >
                                Manage Configuration
                                <ArrowRightIcon className="wf-icon text-secondary" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </div >



          {confirmPlan && (
            <div className="center-form">
              <div style={{ maxWidth: "500px" }}>

                {/* Header */}
                <div className="cf-head">
                  <div>
                    <h2>
                      {SubStaus === "Subscribe Again" ? "Confirm Subscription" : "Confirm Upgrade"}
                    </h2>
                  </div>
                  <button className="cf-close text-secondary" onClick={() => setConfirmPlan(null)}>✕</button>
                </div>

                {/* Body */}
                <div className="cf-body">
                  <p className="fw-semibold d-a gap-1 wf-icon-yellow plain-wh">
                    <InfoIcon className="wf-icon-small" />
                    {SubStaus === "Subscribe Again"
                      ? `Are you sure you want to subscribe to ${confirmPlan.name}?`
                      : `Are you sure you want to upgrade to ${confirmPlan.name}?`}
                  </p>

                  <div className="mt-3 p-3 container-card">
                    <div className="d-flex justify-content-between">
                      <span className="text-secondary">Plan</span>
                      <strong className="text-capitalize">{confirmPlan.name}</strong>
                    </div>

                    <div className="d-flex justify-content-between mt-2">
                      <span className="text-secondary">Price</span>
                      <strong>
                        ${confirmPlan.billing_price}<span className="fw-normal text-secondary text-capitalize"> /{confirmPlan.billing_cycle}</span>
                      </strong>
                    </div>
                  </div>

                  <p className="text-secondary mt-3" style={{ fontSize: "13px" }}>
                    This action will update the tenant subscription immediately.
                  </p>
                </div>

                {/* Footer */}
                <div className="cf-footer d-flex justify-content-between w-100">
                  <button
                    className="container-card px-4 py-2"
                    onClick={() => setConfirmPlan(null)}
                    disabled={upgradingPlanId === confirmPlan.id}
                  >
                    CANCEL
                  </button>

                  <button
                    className="cf-submit bg-primary px-4"
                    onClick={() => {
                      handleUpgradePlan(confirmPlan.id);
                      setConfirmPlan(null);
                    }}
                    disabled={upgradingPlanId === confirmPlan.id}
                  >
                    {upgradingPlanId === confirmPlan.id
                      ? SubStaus === "Subscribe Again"
                        ? "Subscribing..."
                        : "Upgrading..."
                      : SubStaus === "Subscribe Again"
                        ? "Confirm Subscribe"
                        : "Confirm Upgrade"}
                  </button>
                </div>
              </div>
            </div>
          )}





          {billingMissingModal && (
            <div className="center-form">
              <div style={{ maxWidth: "520px", textAlign: "center" }}>
                <div className="cf-head">
                  <div>
                    <h2 className="text-uppercase">Billing Details Required</h2>
                  </div>
                  <button
                    className="cf-close text-secondary"
                    onClick={() => {
                      setBillingMissingModal(false);
                      navigate(-1); // change if needed
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div className="cf-body">
                  <p className="fw-semibold text-danger mb-2">
                    Billing details are missing.
                  </p>

                  <p className="text-secondary" style={{ fontSize: "14px" }}>
                    Billing details are required before upgrading a plan.
                    Please add billing information first and then try again.
                  </p>

                  <div className="mt-3 p-3">
                    <strong>Redirecting to dashboard in {redirectCountdown}s</strong>
                  </div>
                </div>

                <div className="cf-footer d-flex justify-content-center w-100 gap-2">
                  <button
                    className="container-card px-4 py-2"
                    onClick={() => {
                      setBillingMissingModal(false);
                      navigate(-1); // change if needed
                    }}
                  >
                    Go to Dashboard
                  </button>
                </div>
              </div>
            </div>
          )}


          <div className="mobile-view-bottom"></div>
        </>}
    </>
  );
}
